/**
 * Deterministic output-name generation.
 *
 * Aggregation and date-group output names are generated ONCE during plan
 * creation and stored on the plan nodes, so every compiler uses the same names
 * and metadata stays consistent. Collisions with selected/grouped fields are
 * auto-fixed with a numeric suffix.
 */
import type {
    AggregationFunction,
    DateGranularity,
} from '../input/input-types';

export class OutputNameGenerator {
    private readonly reserved = new Set<string>();

    /** Reserve an already-known name (e.g. a selected/grouped field). */
    reserve(name: string): void {
        this.reserved.add(name);
    }

    aggregationName(fn: AggregationFunction, field: string | null): string {
        const base = field
            ? `__${fn}_${field.split('.').join('_')}`
            : `__${fn}`;
        return this.ensureUnique(base);
    }

    dateGroupName(field: string, granularity: DateGranularity | null): string {
        const safe = field.split('.').join('_');
        const base = granularity ? `${safe}_${granularity}` : safe;
        return this.ensureUnique(base);
    }

    private ensureUnique(base: string): string {
        if (!this.reserved.has(base)) {
            this.reserved.add(base);
            return base;
        }
        let suffix = 2;
        let candidate = `${base}_${suffix}`;
        while (this.reserved.has(candidate)) {
            suffix += 1;
            candidate = `${base}_${suffix}`;
        }
        this.reserved.add(candidate);
        return candidate;
    }
}
